import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from '../../hooks/useTranslation';
import * as Icons from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TicketList({ filter }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const ticketsRef = collection(db, 'tickets');
        let q = query(ticketsRef, orderBy('created_at', 'desc'));
        
        // Szűrés hozzáadása ha van
        if (filter) {
          q = query(q, where('status', '==', filter.charAt(0).toUpperCase() + filter.slice(1)));
        }
        
        const snapshot = await getDocs(q);
        setTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [filter]);

  const handleViewTicket = (ticketId) => {
    navigate(`/admin/tickets/${ticketId}`);
  };

  const stats = [
    {
      title: t('tickets.stats.total'),
      value: tickets.length,
      icon: Icons.Inbox,
      color: 'gray'
    },
    {
      title: t('tickets.stats.open'),
      value: tickets.filter(ticket => ticket.status === 'Open').length,
      icon: Icons.MessageCircle,
      color: 'green'
    },
    {
      title: t('tickets.stats.pending'),
      value: tickets.filter(ticket => ticket.status === 'Pending').length,
      icon: Icons.Clock,
      color: 'yellow'
    },
    {
      title: t('tickets.stats.closed'),
      value: tickets.filter(ticket => ticket.status === 'Closed').length,
      icon: Icons.CheckCircle,
      color: 'blue'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('tickets.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('tickets.subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/admin/tickets/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Icons.Plus className="h-4 w-4 mr-2" />
          {t('tickets.newTicket')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className={`h-6 w-6 text-${stat.color}-400`} />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gray-900">{stat.title}</h3>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'id', 'subject', 'customer', 'priority',
                  'status', 'lastUpdate', 'actions'
                ].map(key => (
                  <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t(`tickets.table.${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <tr 
                  key={ticket.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewTicket(ticket.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{ticket.id.slice(-8)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{ticket.subject}</div>
                    <div className="text-sm text-gray-500">{ticket.message?.slice(0, 50)}...</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ticket.customer_name}</div>
                    <div className="text-sm text-gray-500">{ticket.customer_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                      ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      ticket.status === 'Open' ? 'bg-green-100 text-green-800' :
                      ticket.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {ticket.updated_at?.toDate().toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewTicket(ticket.id);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 flex items-center"
                    >
                      <Icons.Eye className="h-4 w-4 mr-1" />
                      {t('tickets.table.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 